import { Box, Dialog } from '@radix-ui/themes'
import { AnimatePresence, motion } from 'framer-motion'
import * as React from 'react'

import { useTranslation } from 'react-i18next'
import { LoginForm } from '#app/components/forms/login-form/login-form.tsx'
import { RegistrationForm } from '#app/components/forms/registration-form/registration-form.tsx'
import { ResetPasswordForm } from '#app/components/forms/reset-password-form/reset-password-form.tsx'
import { CloseButton } from '#app/components/ui/close-button/close-button.tsx'
import { type Locale } from '#app/models/locale'
import {
	type App,
	ROOT_APP_VIEW,
	ROOT_I18NEXT,
} from '#app/routes/_layout/route-config.ts'

/* -------------------------------------------------------------------------------------------------
 * ModalUI
 * -----------------------------------------------------------------------------------------------*/

type ModalUIProps = {
	app: App
	locale: Locale
	setApp: React.Dispatch<React.SetStateAction<App>>
}

const ModalUI: React.FC<ModalUIProps> = ({ app, locale, setApp }) => {
	const [container, setContainer] = React.useState<HTMLElement | null>(null)
	const { t } = useTranslation(ROOT_I18NEXT.routeRoot)

	return (
		<Box className="app-ModalUi">
			<Dialog.Root
				open={app.displayModal}
				onOpenChange={(open) =>
					setApp((prev) => ({ ...prev, displayModal: open }))
				}
			>
				<Dialog.Content container={container}>
					<AnimatePresence mode="wait" initial={false}>
						<motion.main
							key={app.modalView}
							initial={{ y: '-10%', opacity: 0 }}
							animate={{ y: '0', opacity: 1 }}
							exit={{ y: '40%', opacity: 0 }}
						>
							<Dialog.Close>
								<CloseButton
									onClose={() =>
										setApp((prev) => ({
											...prev,
											displayModal: false,
										}))
									}
								/>
							</Dialog.Close>
							<Box hidden>
								<Dialog.Title>{t('modalUi.title')}</Dialog.Title>
								<Dialog.Description>
									{t('modalUi.description')}
								</Dialog.Description>
							</Box>
							{(() => {
								switch (app.modalView) {
									case ROOT_APP_VIEW.loginView:
										return (
											<LoginForm app={app} locale={locale} setApp={setApp} />
										)
									case ROOT_APP_VIEW.registrationView:
										return (
											<RegistrationForm
												app={app}
												locale={locale}
												setApp={setApp}
											/>
										)
									case ROOT_APP_VIEW.resetPasswordView:
										return (
											<ResetPasswordForm
												app={app}
												locale={locale}
												setApp={setApp}
											/>
										)
									default:
										return <></>
								}
							})()}
						</motion.main>
					</AnimatePresence>
				</Dialog.Content>
			</Dialog.Root>
			<Box ref={setContainer} />
		</Box>
	)
}
ModalUI.displayName = 'ModalUI'

/* -----------------------------------------------------------------------------------------------*/

export { ModalUI }
